// Companion/blocks/useBlockAnchor.tsx
//
// A block the student engages with — answering a served question, flipping a
// served flashcard, playing a served video — becomes what the companion is
// anchored to, exactly as the legacy panel's "active tool block" did. The
// anchor channel is ORA-449's (`chatAppContextStore`), so the next turn carries
// it in the app-context envelope. It is the envelope's anchor only: the thread
// the student is in follows the *surface's* anchor, so answering a served
// question mid-conversation never switches them to that question's own thread
// and blanks the one they were reading (ORA-628).
//
// The claim is made **from the engagement event**, not from a render effect.
// `usePublishChatAnchor` re-publishes on every render with no dependency list,
// which is right for a surface that owns the anchor outright but wrong here:
// three blocks can be engaged in one turn, and any re-render of an earlier one
// would republish its anchor and steal the claim back from the block the
// student actually touched last. Publishing on the event makes "last engaged
// wins" true rather than a function of render order.

import { useCallback, useEffect, useRef } from 'react';
import {
  clearBlockChatAnchor,
  setBlockChatAnchor,
} from '@/chatContext/chatAppContextStore';
import type { ChatAnchorInput } from '@/types/chatEnvelope';

export interface BlockAnchor {
  /** Claim the anchor for this block. Read-only blocks never claim. */
  claim: (anchor: ChatAnchorInput | null) => void;
  /**
   * Hand the anchor back to whatever the surface published, without waiting for
   * the block to unmount. A block that puts its content away while staying
   * mounted — a finished study set folded by "Done" (ORA-833) — has stopped
   * being what the next turn is about, and for a video set the claim in force
   * says `playing: true` for a player that has just been taken down.
   */
  retract: () => void;
}

export const useBlockAnchor = (isReadOnly: boolean): BlockAnchor => {
  const claimed = useRef<ChatAnchorInput | null>(null);

  // Retract on unmount, and only our own: a block that has already handed the
  // anchor to another one must not clear the new value when the thread goes.
  useEffect(() => () => clearBlockChatAnchor(claimed.current), []);

  const claim = useCallback(
    (anchor: ChatAnchorInput | null) => {
      if (isReadOnly || !anchor) return;

      claimed.current = anchor;
      setBlockChatAnchor(anchor);
    },
    [isReadOnly]
  );

  // Forgetting the claim is part of retracting it: the unmount cleanup must not
  // retract a second time over an anchor another block has since claimed.
  const retract = useCallback(() => {
    clearBlockChatAnchor(claimed.current);
    claimed.current = null;
  }, []);

  return { claim, retract };
};
